"use client"

import React, { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/navigation"
import {
  addOpenTenderPaymentAction,
  addPaymentInstrumentPaymentAction,
  loadBasketPaymentMethodsAction,
  removePaymentInstrumentAction,
} from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { BasketCtnrROV1IncludedPaymentsPaymentInstrument, PaymentMethodROV1 } from "@ub/basket-client"
import { useTranslations } from "next-intl"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { CheckoutContext } from "@/lib/storefront/checkout/checkout-service"
import { AccordionContent, AccordionItem, AccordionRoot, AccordionTrigger, Button } from "@/components/ui/components"
import CreditCheckConsent from "@/components/checkout/payment/CreditCheckConsent"
import PayoneCreditCardHostedIFrames from "@/components/checkout/payment/payone/PayoneCreditCardHostedIFrames"
import PayoneEpsBankGroupSelectBox from "@/components/checkout/payment/payone/PayoneEpsBankGroupSelectBox"
import BasicCard from "@/components/common/BasicCard"
import { UbCheckoutPaymentViewStoryblok, UbPaymentMethodContentStoryblok } from "@/components/storyblok/component-types"

const PAYONE_CREDITCARD_SERVICE_ID = "Payone_CreditCard"
const PAYONE_EPS_SERVICE_ID = "Payone_Eps"

interface CheckoutPaymentProps {
  blok: UbCheckoutPaymentViewStoryblok
  checkoutContext: CheckoutContext
  paymentMethodLogosById: Record<string, React.ReactNode>
}

export default function CheckoutPaymentClient(props: CheckoutPaymentProps) {
  const t = useTranslations("storefront")

  const router = useRouter()
  const { basketData, creditCheckConsentChecked, setCreditCheckConsentChecked } = useUbStorefrontState()
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodROV1[]>([])
  const [paymentInstruments, setPaymentInstruments] = useState<BasketCtnrROV1IncludedPaymentsPaymentInstrument>({})
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [showCreditCheckConsentRequired, setShowCreditCheckConsentRequired] = useState<boolean>(false)

  const hasOrders = props.checkoutContext.numberOfOrders > 0

  const creditCheckConsentRequired = useMemo(() => {
    return !!(props.blok.showConsent && props.checkoutContext.isB2B && !hasOrders)
  }, [props.blok.showConsent, hasOrders, props.checkoutContext.isB2B])

  const isB2B = props.checkoutContext.isB2B

  //only for multiple accordion const [openItems, setOpenItems] = useState<string[]>([])

  const [openItem, setOpenItem] = useState<string | undefined>(undefined)

  const paymentMethodContentById: Record<string, UbPaymentMethodContentStoryblok> = useMemo(() => {
    const record: Record<string, UbPaymentMethodContentStoryblok> = {}
    if (typeof props.blok?.payment_method_content?.[0]?.reference !== "string") {
      props.blok?.payment_method_content?.[0]?.reference?.content?.body.forEach(
        (value: UbPaymentMethodContentStoryblok) => {
          record[value.identifier as string] = value
        }
      )
    }
    return record
  }, [props.blok])

  const handleValueChange = (value: string) => {
    setOpenItem(value)
  }

  const onCreditCheckConsentChange = (event) => {
    const checked = event.target.checked
    setCreditCheckConsentChecked(checked)
    setShowCreditCheckConsentRequired(!checked)
  }

  useEffect(() => {
    loadBasketPaymentMethodsAction(isB2B).then(async (paymentMethods) => {
      if (paymentMethods) {
        const pmd = paymentMethods.data ?? []
        setPaymentMethods(pmd)
        setPaymentInstruments(paymentMethods.included?.paymentInstruments ?? {})
        const pmids = pmd.map((pm: PaymentMethodROV1) => {
          return pm?.serviceID ?? ""
        })
        if (pmids && pmids.length > 0) {
          setOpenItem(pmids[0])
        }
      }
    })
  }, [basketData, isB2B])

  function removePaymentInstrument(paymentInstrument: string | undefined) {
    if (paymentInstrument) {
      removePaymentInstrumentAction(paymentInstrument, isB2B).then((value) => {
        if (value.result === "NOK") {
          setErrorMessage(value.message)
        } else {
          const copyOfPaymentInstruments = { ...paymentInstruments }
          delete copyOfPaymentInstruments[paymentInstrument]
          setPaymentInstruments((paymentInstrument) => ({ ...copyOfPaymentInstruments }))
        }
      })
    }
  }

  function addPayment(paymentInstrument: string | undefined) {
    if (creditCheckConsentRequired && !creditCheckConsentChecked) {
      setShowCreditCheckConsentRequired(true)
      return
    }
    if (paymentInstrument) {
      addOpenTenderPaymentAction(paymentInstrument, isB2B).then((value) => {
        if (value.result === "NOK") {
          setErrorMessage(value.message)
        } else {
          router.push(getLinkWithPrefixFromURLAndLocale("/basket/checkout/review", props.checkoutContext.isoLocale))
        }
      })
    }
  }

  function addPaymentAction(formData: FormData) {
    if (creditCheckConsentRequired && !creditCheckConsentChecked) {
      setShowCreditCheckConsentRequired(true)
      return
    }
    const paymentMethodID = formData.get("paymentMethodID") as unknown as string
    const paymentMethodServiceID = paymentMethods?.find(
      (paymentMethod) => paymentMethod.id === paymentMethodID
    )?.serviceID
    let cont = true
    let parameters = {}
    switch (paymentMethodServiceID) {
      case PAYONE_CREDITCARD_SERVICE_ID: {
        const pseudocardpan = formData.get("pseudocardpan") as object
        const truncatedcardpan = formData.get("truncatedcardpan") as object
        if (pseudocardpan && truncatedcardpan) {
          parameters = {
            pseudocardpan: pseudocardpan,
            truncatedcardpan: truncatedcardpan,
          }
        }
        break
      }
      case PAYONE_EPS_SERVICE_ID: {
        const bankGroupCode = formData.get("bankGroupCode") as object
        if (bankGroupCode) {
          parameters = {
            bankGroupCode: bankGroupCode,
          }
        } else {
          cont = false
        }
        break
      }
      default:
        break
    }
    if (cont) {
      addPaymentInstrumentPaymentAction(paymentMethodID, parameters, isB2B).then((value) => {
        if (value.result === "NOK") {
          setErrorMessage(value.message)
        } else {
          router.push(getLinkWithPrefixFromURLAndLocale("/basket/checkout/review", props.checkoutContext.isoLocale))
        }
      })
    }
  }

  const getIncludedPaymentInstrumentIds = (paymentMethod: PaymentMethodROV1) => {
    return paymentMethod.paymentInstruments?.filter((paymentInstrument) => paymentInstruments[paymentInstrument]) ?? []
  }

  const getPayoneEpsElement = (paymentMethod: PaymentMethodROV1) => {
    const includedPaymentInstrumentIds = getIncludedPaymentInstrumentIds(paymentMethod)
    return includedPaymentInstrumentIds.length ? (
      <>
        {includedPaymentInstrumentIds.map((paymentInstrumentId, index) => (
          <div key={"instrument-" + index}>
            <div className={"flex flex-row items-center gap-2"}>
              <div>{paymentInstruments[paymentInstrumentId].accountIdentifier}</div>
              <Button
                size={"default"}
                variant={"default"}
                className={"cursor-pointer bg-primary py-2 font-normal text-white"}
                type={"button"}
                onClick={() => removePaymentInstrument(paymentInstrumentId)}
              >
                {t("checkout.payment.instrument.remove")}
              </Button>
            </div>
            <div className={"flex flex-col justify-end gap-2 lg:flex-row"}>
              {/*        <CreditCheckConsent
                checked={creditCheckConsentChecked}
                onChange={onCreditCheckConsentChange}
                show={creditCheckConsentRequired}
                text={props.blok.consent_text}
                showRequired={showCreditCheckConsentRequired}
                requiredText={props.blok.consent_required_text}
              />*/}
              <Button
                size={"default"}
                variant={"default"}
                className={"cursor-pointer bg-primary py-2 font-normal text-white"}
                type={"button"}
                disabled={creditCheckConsentRequired && !creditCheckConsentChecked}
                onClick={() => addPayment(paymentInstrumentId)}
              >
                {t("checkout.payment.button")}
              </Button>
            </div>
          </div>
        ))}
      </>
    ) : (
      <>
        <div className={"flex flex-row items-center gap-2"}>
          <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.eps.bankGroup")}</h2>
          <PayoneEpsBankGroupSelectBox paymentMethod={paymentMethod} isoLocale={props.checkoutContext.isoLocale} />
        </div>
        <div className={"flex flex-col justify-end gap-2 lg:flex-row"}>
          {/*   <CreditCheckConsent
            checked={creditCheckConsentChecked}
            onChange={onCreditCheckConsentChange}
            show={creditCheckConsentRequired}
            text={props.blok.consent_text}
            showRequired={showCreditCheckConsentRequired}
            requiredText={props.blok.consent_required_text}
          />*/}
          <Button
            size={"default"}
            variant={"default"}
            className={"cursor-pointer bg-primary py-0 font-normal text-white"}
            type={"button"}
            disabled={creditCheckConsentRequired && !creditCheckConsentChecked}
            onClick={() => {
              const paymentForm = document.querySelector<HTMLFormElement>('form[name="PaymentForm"]')
              const paymentMethodInput = document.querySelector<HTMLInputElement>('input[name="paymentMethodID"]')
              if (paymentForm && paymentMethodInput) {
                paymentMethodInput.value = paymentMethod.id as string
                paymentForm.requestSubmit()
              }
            }}
          >
            {t("checkout.payment.button")}
          </Button>
        </div>
      </>
    )
  }

  const getPayoneCreditCardElement = (paymentMethod: PaymentMethodROV1) => {
    /*Override payment description if given in storyblok*/
    const effectiveDescription =
      paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.description &&
      paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.description !== ""
        ? paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.description
        : paymentMethod.description

    return (
      <>
        <PayoneCreditCardHostedIFrames
          paymentMethod={paymentMethod}
          descriptionText={effectiveDescription}
          creditCheckConsentChecked={creditCheckConsentChecked}
          onCreditCheckConsentChange={onCreditCheckConsentChange}
          showCreditCheckConsent={creditCheckConsentRequired}
          creditCheckConsentText={props.blok.consent_text}
          showCreditCheckConsentRequired={showCreditCheckConsentRequired}
          creditCheckConsentRequiredText={props.blok.consent_required_text}
        />
        {getIncludedPaymentInstrumentIds(paymentMethod).map((paymentInstrumentId, index) => (
          <div key={"instrument-" + index}>
            <hr className={"m-4"} />
            <div className={"flex flex-col gap-2"}>
              <h2 className={"text-large font-bold"}>{t("checkout.payment.payone.creditcard.cardnumber")}</h2>
              <div className={"flex flex-row items-center gap-2"}>
                <div>{paymentInstruments[paymentInstrumentId].accountIdentifier}</div>
                <Button
                  size={"default"}
                  variant={"default"}
                  className={"cursor-pointer bg-primary py-0 font-normal text-white"}
                  type={"button"}
                  onClick={() => removePaymentInstrument(paymentInstrumentId)}
                >
                  {t("checkout.payment.instrument.remove")}
                </Button>
              </div>
              <div className={"flex flex-col justify-end gap-2 lg:flex-row"}>
                {/*  <CreditCheckConsent
                  checked={creditCheckConsentChecked}
                  onChange={onCreditCheckConsentChange}
                  show={creditCheckConsentRequired}
                  text={props.blok.consent_text}
                  showRequired={showCreditCheckConsentRequired}
                  requiredText={props.blok.consent_required_text}
                />*/}
                <Button
                  size={"default"}
                  variant={"default"}
                  className={"cursor-pointer bg-primary py-2 font-normal text-white"}
                  type={"button"}
                  disabled={creditCheckConsentRequired && !creditCheckConsentChecked}
                  onClick={() => addPayment(paymentInstrumentId)}
                >
                  {t("checkout.payment.button")}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <div className={"flex w-full flex-row"}>
      <div className={"flex w-full flex-col gap-2"}>
        {/*
        <h2 className={"text-lg font-bold"}>{t("checkout.payment.cta")}</h2>
*/}
        {errorMessage && <p className={"text-xs text-red-500"}>{errorMessage}</p>}
        {paymentMethods && (
          <form name={"PaymentForm"} action={addPaymentAction}>
            <input type={"hidden"} name={"paymentMethodID"} id={"paymentMethodID"} />
            <AccordionRoot
              type={"single"}
              collapsible={true}
              className={"w-full"}
              value={openItem}
              onValueChange={handleValueChange}
            >
              {paymentMethods.map((paymentMethod) => (
                <AccordionItem className={"pb-4"} key={paymentMethod.serviceID} value={paymentMethod.serviceID ?? ""}>
                  <AccordionTrigger className={"w-full bg-white px-2 py-7 text-lg font-bold hover:bg-gray-200 md:px-3"}>
                    <div className={"flex w-full flex-row justify-between gap-2 "}>
                      <p>
                        {paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.title ?? paymentMethod?.serviceID}
                      </p>

                      <div className={"pr-4"}>{props.paymentMethodLogosById[paymentMethod?.serviceID ?? ""]}</div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className={"bg-white p-2 md:p-4"}>
                    <BasicCard className={"min-h-24 border-none p-1 text-sm md:p-2 md:text-base"}>
                      {paymentMethod.serviceID === PAYONE_CREDITCARD_SERVICE_ID ? (
                        getPayoneCreditCardElement(paymentMethod)
                      ) : (
                        <div className={"flex h-full min-h-24 flex-col justify-between gap-2 text-sm md:flex-row"}>
                          <div className={"flex flex-col gap-2"}>
                            <div>
                              {
                                /*Override payment description if given in storyblok*/
                                paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.description &&
                                paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.description !== ""
                                  ? paymentMethodContentById[paymentMethod?.serviceID ?? ""]?.description
                                  : paymentMethod.description
                              }
                            </div>
                            <CreditCheckConsent
                              checked={creditCheckConsentChecked}
                              onChange={onCreditCheckConsentChange}
                              show={creditCheckConsentRequired}
                              text={props.blok.consent_text}
                              showRequired={showCreditCheckConsentRequired}
                              requiredText={props.blok.consent_required_text}
                            />
                          </div>
                          {paymentMethod.serviceID === PAYONE_EPS_SERVICE_ID ? (
                            getPayoneEpsElement(paymentMethod)
                          ) : (
                            <>
                              <div className={"flex flex-col justify-end gap-2 lg:flex-row"}>
                                {/* <CreditCheckConsent
                                  checked={creditCheckConsentChecked}
                                  onChange={onCreditCheckConsentChange}
                                  show={creditCheckConsentRequired}
                                  text={props.blok.consent_text}
                                  showRequired={showCreditCheckConsentRequired}
                                  requiredText={props.blok.consent_required_text}
                                />*/}
                                <Button
                                  size={"default"}
                                  variant={"default"}
                                  className={"cursor-pointer bg-primary py-2 font-normal text-white"}
                                  type={"button"}
                                  disabled={creditCheckConsentRequired && !creditCheckConsentChecked}
                                  onClick={() => addPayment(paymentMethod.paymentInstruments?.[0])}
                                >
                                  {t("checkout.payment.button")}
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </BasicCard>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </AccordionRoot>
          </form>
        )}
      </div>
    </div>
  )
}
